import { useEffect, useState } from "react";
import { BRAND_LOGO } from "./brand";

let cachedDataUrl = null;
let pendingPromise = null;

/**
 * Fetches the brand logo once and converts it to a base64 data URL so that
 * any component that needs to rasterize (html-to-image, canvas) is free of
 * cross-origin restrictions. Cached at module level.
 */
export async function loadBrandLogoDataUrl() {
  if (cachedDataUrl) return cachedDataUrl;
  if (pendingPromise) return pendingPromise;
  pendingPromise = (async () => {
    try {
      const res = await fetch(BRAND_LOGO, { mode: "cors" });
      const blob = await res.blob();
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      cachedDataUrl = dataUrl;
      return dataUrl;
    } catch {
      // Fall back to the original URL — components can still render normally,
      // export will be the only path affected if CORS is blocked.
      cachedDataUrl = BRAND_LOGO;
      return BRAND_LOGO;
    } finally {
      pendingPromise = null;
    }
  })();
  return pendingPromise;
}

/**
 * Hook: returns the brand logo as a data URL once loaded. Until loaded,
 * returns the original CDN URL so the UI doesn't flicker.
 */
export function useBrandLogo() {
  const [src, setSrc] = useState(cachedDataUrl || BRAND_LOGO);
  useEffect(() => {
    if (cachedDataUrl) { setSrc(cachedDataUrl); return; }
    let cancelled = false;
    loadBrandLogoDataUrl().then((url) => { if (!cancelled) setSrc(url); });
    return () => { cancelled = true; };
  }, []);
  return src;
}

/** Wait for web fonts + a short tick so layout settles before rasterization. */
export async function waitForRender(delayMs = 60) {
  try {
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
  } catch {}
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  if (delayMs > 0) await new Promise((r) => setTimeout(r, delayMs));
}
